// src/components/Sidebar.jsx
import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import SOURCE_MAP from "../config/sourceMap";
import {
  Package,
  Plus,
  Upload,
  RotateCcw,
  ShoppingCart,
  Store,
  Tag,
  Search,
  FileText,
  Download,
  Wrench,
  BarChart3,
  IndianRupee,
  AlertCircle,
  Wallet,
  User,
  ChevronDown,
  ChevronRight,
  PackageX,
  ChevronLeft,
  Sparkles,
  Star,
  MessageSquare,
  SlidersHorizontal,
  Mail,
  FileEdit,
  Settings,
} from "lucide-react";

export default function Sidebar({ isOpen, isCollapsed, onCollapse }) {
  const [acctOpen, setAcctOpen] = useState(false);
  const [source, setSource] = useState(() => localStorage.getItem("source") || "wayuvega");
  const [hoveredItem, setHoveredItem] = useState(null);
  
  // State for collapsible sections - Orders Management expanded by default
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem("expandedSections");
    return saved ? JSON.parse(saved) : ["Analytics", "Orders Management"];
  });
  
  // State for starred items
  const [starredItems, setStarredItems] = useState(() => {
    const saved = localStorage.getItem("starredItems");
    return saved ? JSON.parse(saved) : [];
  });

  // Icon mapping for routes
  const iconMap = {
    "All Shipments": Package,
    "Create Forward Order": Plus,
    "Create Bulk Order": Upload,
    "Create Reverse Order": RotateCcw,
    "Shopify Orders": ShoppingCart,
    "Marketplace Self-Ship Orders": Store,
    "Generate Label": Tag,
    "Bulk Tracking": Search,
    "Bulk Labels Download": Download,
    "Bulk Invoice Download": FileText,
    "Tools": Wrench,
    "Claim": AlertCircle,
    "Reports": BarChart3,
    "COD Remittance Report": IndianRupee,
    "Unprocessed Orders": AlertCircle,
    "Account Summary": Wallet,
    "Wallet": Wallet,
    "Profile": User,
    "Pickup Manifest": FileText,
    "NDR": PackageX,  // Added NDR icon
    "Support Tickets": MessageSquare,
    "Report Configuration": SlidersHorizontal,
    "Scheduled Email Reports": Mail,
    "Draft Orders": FileEdit,
    "Account Configuration": Settings,
  };

  const routes = [
    {
      section: "Orders Management",
      items: [
        { path: "/all-shipments", name: "All Shipments" },
        { path: "/draft-orders", name: "Draft Orders" },
        { path: "/create-forward-order", name: "Create Forward Order" },
        { path: "/create-bulk-order", name: "Create Bulk Order" },
        { path: "/create-reverse-order", name: "Create Reverse Order" },
        { path: "/pickup-manifest", name: "Pickup Manifest" },
        { path: "/ndr-management", name: "NDR" },
        { path: "/claim-tracker", name: "Claim" },
      ]
    },
    {
      section: "Marketplace",
      items: [
        { path: "/channel-order", name: "Shopify Orders" },
        { path: "/marketplace-self-ship", name: "Marketplace Self-Ship Orders" },
      ]
    },
    {
      section: "Operations",
      items: [
        { path: "/bulk-tracking", name: "Bulk Tracking" },
        { path: "/shipping-bulk-labels-download", name: "Bulk Labels Download" },
        { path: "/bulk-invoice-download", name: "Bulk Invoice Download" },
        { path: "/tools", name: "Tools" },
        { path: "/search-unprocessed-orders", name: "Unprocessed Orders" },
        { path: "/support/tickets", name: "Support Tickets" },
      ]
    },
    {
      section: "Reports & Finance",
      items: [
        { path: "/reports", name: "Reports" },
        { path: "/cod-remittance-reports", name: "COD Remittance Report" },
        { path: "/wallet", name: "Wallet" },
      ]
    },
    {
      section: "Control Center",
      items: [
        { path: "/settings/configuration", name: "Account Configuration" },
        { path: "/settings/report-configuration", name: "Report Configuration" },
        { path: "/settings/scheduled-email-reports", name: "Scheduled Email Reports" },
      ]
    },
    {
      section: "Settings",
      items: [
        { path: "/profile", name: "Profile" },
      ]
    }
  ];

  // Save expanded sections to localStorage
  useEffect(() => {
    localStorage.setItem("expandedSections", JSON.stringify(expandedSections));
  }, [expandedSections]);
  
  // Save starred items to localStorage
  useEffect(() => {
    localStorage.setItem("starredItems", JSON.stringify(starredItems));
  }, [starredItems]);

  useEffect(() => {
    const handler = (e) => {
      if (!e) return;
      if (e.key === "source") {
        setSource(e.newValue || "wayuvega");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const brand = SOURCE_MAP[source] || SOURCE_MAP.wayuvega;
  
  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => {
      if (prev.includes(sectionName)) {
        return prev.filter(s => s !== sectionName);
      } else {
        return [...prev, sectionName];
      }
    });
  };
  
  // Toggle starred status
  const toggleStar = (path, e) => {
    e.preventDefault();
    e.stopPropagation();
    setStarredItems(prev => {
      if (prev.includes(path)) {
        return prev.filter(p => p !== path);
      } else {
        return [...prev, path];
      }
    });
  };

  const containerStyle = (() => {
    if (source === "jumbomail") {
      return "bg-gradient-to-b from-[#F8EFE6] via-[#F0F4F8] to-[#E8F6FB]";
    }
    return "bg-gradient-to-b from-slate-50 via-gray-50 to-slate-100";
  })();

  const linkActiveClass = source === "jumbomail"
    ? "flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#07374a] to-[#0a4a5e] text-white shadow-lg shadow-[#07374a]/20 font-semibold transition-all transform scale-[1.02]"
    : "flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30 font-semibold transition-all transform scale-[1.02]";

  const linkDefaultClass = source === "jumbomail"
    ? "flex items-center gap-3 p-3 rounded-xl hover:bg-white/60 hover:shadow-md transition-all text-[#07374a] font-medium"
    : "flex items-center gap-3 p-3 rounded-xl hover:bg-white/70 hover:shadow-md transition-all text-gray-700 font-medium";

  const sublinkActiveClass = source === "jumbomail"
    ? "flex items-center gap-3 p-2.5 pl-11 rounded-lg bg-gradient-to-r from-[#07374a] to-[#0a4a5e] text-white shadow-md font-medium transition-all"
    : "flex items-center gap-3 p-2.5 pl-11 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md font-medium transition-all";

  const sublinkDefaultClass = source === "jumbomail"
    ? "flex items-center gap-3 p-2.5 pl-11 rounded-lg hover:bg-white/50 transition-all text-[#07374a]/80"
    : "flex items-center gap-3 p-2.5 pl-11 rounded-lg hover:bg-white/60 transition-all text-gray-600";

  const expandButtonClass = source === "jumbomail"
    ? "flex items-center justify-between gap-3 w-full p-3 rounded-xl hover:bg-white/60 hover:shadow-md transition-all text-[#07374a] font-medium cursor-pointer"
    : "flex items-center justify-between gap-3 w-full p-3 rounded-xl hover:bg-white/70 hover:shadow-md transition-all text-gray-700 font-medium cursor-pointer";

  const getIcon = (name) => {
    const IconComponent = iconMap[name];
    return IconComponent ? <IconComponent className="w-5 h-5 flex-shrink-0" strokeWidth={2} /> : null;
  };
  
  // Get starred routes for the starred section
  const getStarredRoutes = () => {
    const allRoutes = routes.flatMap(section => section.items);
    return allRoutes.filter(route => starredItems.includes(route.path));
  };
  
  // Render a single route item
  const renderRouteItem = (route, i, showStar = true) => (
    <li key={`${route.path}-${i}`} className="relative group">
      {!route.subRoutes ? (
        <>
          <NavLink
            to={route.path}
            end
            className={({ isActive }) => (isActive ? linkActiveClass : linkDefaultClass)}
          >
            {getIcon(route.name)}
            <span className={`flex-1 min-w-0 transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:w-0 md:overflow-hidden' : 'md:opacity-100'}`}>
              {route.name}
            </span>
            {!isCollapsed && showStar && (
              <button
                onClick={(e) => toggleStar(route.path, e)}
                className={`p-1 rounded hover:bg-white/30 transition-all ${
                  starredItems.includes(route.path) ? 'text-yellow-400' : 'text-gray-400 opacity-0 group-hover:opacity-100'
                }`}
              >
                <Star 
                  className="w-4 h-4" 
                  fill={starredItems.includes(route.path) ? "currentColor" : "none"}
                  strokeWidth={2}
                />
              </button>
            )}
          </NavLink>
          
          {/* Tooltip for collapsed state - Desktop only */}
          {isCollapsed && (
            <div className="tooltip hidden md:group-hover:block absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl whitespace-nowrap">
              {route.name}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
            </div>
          )}
        </>
      ) : (
        <div>
          <button
            onClick={() => !isCollapsed && setAcctOpen(!acctOpen)}
            className={`${expandButtonClass} ${isCollapsed ? 'md:justify-center' : ''}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {getIcon(route.name)}
              <span className={`flex-1 transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:w-0 md:overflow-hidden' : 'md:opacity-100'}`}>
                {route.name}
              </span>
            </div>
            {!isCollapsed && (
              acctOpen ? (
                <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform" strokeWidth={2.5} />
              ) : (
                <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform" strokeWidth={2.5} />
              )
            )}
          </button>
          
          {/* Tooltip for collapsed state - Desktop only */}
          {isCollapsed && (
            <div className="tooltip hidden md:group-hover:block absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl whitespace-nowrap">
              {route.name}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
            </div>
          )}
          
          {acctOpen && !isCollapsed && (
            <ul className="mt-1.5 space-y-1 animate-slideDown">
              {route.subRoutes.map((sub, j) => (
                <li key={j}>
                  <NavLink
                    to={sub.path}
                    className={({ isActive }) =>
                      sub.disabled 
                        ? "flex items-center gap-3 p-2.5 pl-11 rounded-lg bg-gray-200 text-gray-400 cursor-not-allowed"
                        : (isActive ? sublinkActiveClass : sublinkDefaultClass)
                    }
                  >
                    {getIcon(sub.name)}
                    <span className="flex-1">{sub.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );

  return (
    <>
      <style>{`
        .sidebar-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .sidebar-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 3px;
        }
        .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.2);
        }
        .tooltip {
          pointer-events: none;
          white-space: nowrap;
        }
      `}</style>
      
      <nav
        className={`
          ${containerStyle}
          ${isCollapsed ? 'w-20' : 'w-64'}
          fixed top-16 left-0
          h-[calc(100vh-4rem)] z-30
          transform transition-all duration-300 ease-in-out
          ${isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
          md:translate-x-0 md:shadow-lg
          border-r border-gray-200/50
        `}
      >
        {/* Desktop Collapse Toggle - Only visible on desktop */}
        <button
          onClick={onCollapse}
          className={`
            hidden md:flex absolute -right-3 top-6 z-40
            w-6 h-6 items-center justify-center
            bg-white rounded-full shadow-lg border border-gray-200
            hover:bg-gray-50 hover:scale-110 transition-all duration-200
            ${source === "jumbomail" ? "text-[#07374a] hover:text-[#0a4a5e]" : "text-gray-700 hover:text-blue-600"}
          `}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          ) : (
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          )}
        </button>

        <div className="h-full overflow-y-auto sidebar-scrollbar p-4">
          <div className="space-y-6">
            {/* Starred Section */}
            {starredItems.length > 0 && !isCollapsed && (
              <div>
                <div className="px-3 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  <h3 className={`text-xs font-bold uppercase tracking-wider ${
                    source === "jumbomail" ? "text-[#07374a]/60" : "text-gray-500"
                  }`}>
                    Starred
                  </h3>
                </div>
                <ul className="space-y-1.5">
                  {getStarredRoutes().map((route, i) => renderRouteItem(route, i, true))}
                </ul>
              </div>
            )}

            {/* Regular Sections */}
            {routes.map((section, sectionIdx) => {
              const isExpanded = expandedSections.includes(section.section);
              
              return (
                <div key={sectionIdx}>
                  {/* Section Header - Clickable to expand/collapse */}
                  {!isCollapsed ? (
                    <button
                      onClick={() => toggleSection(section.section)}
                      className="w-full px-3 mb-2 flex items-center justify-between group cursor-pointer hover:bg-white/30 rounded-lg p-1.5 transition-all"
                    >
                      <h3 className={`text-xs font-bold uppercase tracking-wider ${
                        source === "jumbomail" ? "text-[#07374a]/60" : "text-gray-500"
                      }`}>
                        {section.section}
                      </h3>
                      <div className={`transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}>
                        <ChevronDown className={`w-4 h-4 ${
                          source === "jumbomail" ? "text-[#07374a]/40" : "text-gray-400"
                        }`} strokeWidth={2} />
                      </div>
                    </button>
                  ) : (
                    // Section Divider for collapsed state
                    sectionIdx > 0 && (
                      <div className="my-3 mx-auto w-8 h-px bg-gray-300"></div>
                    )
                  )}

                  {/* Section Items - Only show if expanded or sidebar is collapsed */}
                  {(isExpanded || isCollapsed) && (
                    <ul className="space-y-1.5">
                      {section.items.map((route, i) => renderRouteItem(route, i, true))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}